import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { CmsMoveDialogProps } from './types';
import Pages from '../../pages/cms/Pages';
import { useState } from 'react';
import { PageListItem } from '../../api/types/cms';
import useApi from '../../api';
import { useResponseHandler } from '../../utils/response';
import { useSnackbar } from 'notistack';
import { useTranslation } from '../../contexts/TranslationContext';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { CloseOutlined, RightOutlined } from '@ant-design/icons';
import { useTheme } from '@mui/material/styles';

export default function CmsMoveDialog({
    page,
    title,
    children,
    submitDescription = 'pages.cms.pages.move.move_btn',
    cancelDescription = 'form.general.cancel',
    onClose = () => {},
    onMoved = () => {},
}: CmsMoveDialogProps) {
    const theme = useTheme();
    const { handleResponse } = useResponseHandler();
    const { cms } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { translate } = useTranslation();

    const [selectedPage, setSelectedPage] = useState<PageListItem>();

    const handleClose = () => {
        onClose && onClose();
    };

    const selectNewParent = () => {
        if (selectedPage && page?.id) {
            cms.updatePage(page.id, { parent: selectedPage.id }).then(
                response => {
                    handleResponse(response, () => {
                        enqueueSnackbar(translate('messages.saved'), {
                            variant: 'success',
                        });
                        onMoved();
                        handleClose();
                    });
                },
            );
        }
    };

    const disabled = page?.id === selectedPage?.id || page?.home_page;

    return (
        <React.Fragment>
            <Dialog
                open={Boolean(page)}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title" variant="h5">
                    {title}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={4}>
                        <Stack spacing={2}>
                            {children}
                            {page && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}>
                                    <Card elevation={0}>
                                        <CardContent>
                                            <Typography variant="h5">
                                                {page.title}
                                            </Typography>
                                            <Typography variant="caption">
                                                {page.id}
                                            </Typography>
                                            <Typography variant="subtitle2">
                                                {page.slug}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card
                                        sx={{
                                            height: 175,
                                            width: 175,
                                            px: 2,
                                            background: disabled
                                                ? theme.palette.error.dark
                                                : undefined,
                                            borderRadius: 100,
                                        }}
                                        variant="elevation"
                                        elevation={0}>
                                        <Stack
                                            spacing={2}
                                            alignItems="center"
                                            justifyContent="center"
                                            height="100%">
                                            {!disabled && (
                                                <>
                                                    <RightOutlined
                                                        style={{ fontSize: 24 }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        textAlign="center">
                                                        {translate(
                                                            'pages.cms.pages.move.can_move',
                                                        ).toLowerCase()}
                                                    </Typography>
                                                </>
                                            )}
                                            {disabled && (
                                                <>
                                                    <CloseOutlined
                                                        style={{ fontSize: 24 }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        textAlign="center">
                                                        {translate(
                                                            'pages.cms.pages.move.cant_move',
                                                        ).toLowerCase()}
                                                    </Typography>
                                                </>
                                            )}
                                        </Stack>
                                    </Card>
                                    <Card elevation={0}>
                                        <CardContent>
                                            <Typography variant="h5">
                                                {selectedPage?.title}
                                            </Typography>
                                            <Typography variant="caption">
                                                {selectedPage?.id}
                                            </Typography>
                                            <Typography variant="subtitle2">
                                                {selectedPage?.slug}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Stack>
                            )}
                        </Stack>
                        <Pages
                            withToolbar={false}
                            withMoveModal={false}
                            allowActions={false}
                            onPageSelect={setSelectedPage}
                            overrideLocale={page?.locale}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="text">
                        {translate(cancelDescription)}
                    </Button>
                    <Button
                        onClick={selectNewParent}
                        autoFocus
                        variant="contained"
                        disabled={disabled}>
                        {translate(submitDescription)}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
